import React from "react";
import { BodyDoc, BodyNode, BodyMark } from "@common/interfaces/KYDCampaign";
import { BoxProps } from "@chakra-ui/react";

interface BodyDocRendererProps {
  content: BodyDoc;
  isMobile?: boolean;
}

const BodyDocRenderer: React.FC<
  BodyDocRendererProps & Omit<BoxProps, "content">
> = ({ content, isMobile = false, ...props }) => {
  if (!content || !content.content) {
    return null;
  }

  const renderMarks = (
    text: string,
    marks: BodyMark[] = []
  ): React.ReactNode => {
    let result: React.ReactNode = text;

    marks.forEach((mark) => {
      switch (mark.type) {
        case "bold":
          result = <strong>{result}</strong>;
          break;
        case "italic":
          result = <em>{result}</em>;
          break;
        case "strike":
          result = <s>{result}</s>;
          break;
        case "underline":
          result = <u>{result}</u>;
          break;
        case "code":
          result = <code>{result}</code>;
          break;
        case "link":
          const href = mark.attrs?.href || "#";
          result = (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="editor-link"
            >
              {result}
            </a>
          );
          break;
        case "textStyle":
          const color = mark.attrs?.color;
          const backgroundColor = mark.attrs?.backgroundColor;
          const fontSize = mark.attrs?.fontSize;
          const fontFamily = mark.attrs?.fontFamily;
          const textDecoration = mark.attrs?.textDecoration;

          const style: React.CSSProperties = {};
          if (color) style.color = color;
          if (backgroundColor) style.backgroundColor = backgroundColor;
          if (fontSize) style.fontSize = fontSize;
          if (fontFamily) style.fontFamily = fontFamily;
          if (textDecoration) style.textDecoration = textDecoration;

          if (Object.keys(style).length > 0) {
            result = <span style={style}>{result}</span>;
          }
          break;
        case "highlight":
          const highlightColor = mark.attrs?.color || "#ffeb3b";
          result = (
            <mark style={{ backgroundColor: highlightColor }}>{result}</mark>
          );
          break;
        case "subscript":
          result = <sub>{result}</sub>;
          break;
        case "superscript":
          result = <sup>{result}</sup>;
          break;
        case "small":
          result = <small>{result}</small>;
          break;
        default:
          break;
      }
    });

    return result;
  };

  const renderNode = (node: BodyNode): React.ReactNode => {
    if (node.text) {
      return renderMarks(node.text, node.marks);
    }

    // Handle empty paragraphs and other block elements that should render even when empty
    if (
      node.type === "paragraph" &&
      (!node.content || node.content.length === 0)
    ) {
      const textAlign = node.attrs?.textAlign;
      const paragraphStyle: React.CSSProperties = {};
      if (textAlign) paragraphStyle.textAlign = textAlign as any;
      if (node.attrs?.color) paragraphStyle.color = node.attrs.color;
      if (node.attrs?.backgroundColor)
        paragraphStyle.backgroundColor = node.attrs.backgroundColor;

      return (
        <p
          style={
            Object.keys(paragraphStyle).length > 0 ? paragraphStyle : undefined
          }
          data-text-align={textAlign}
        >
          &nbsp;
        </p>
      );
    }

    if (node.content) {
      const children = node.content.map((child, index) => (
        <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
      ));

      switch (node.type) {
        case "doc":
          return <div className="editor-container">{children}</div>;

        case "paragraph":
          const textAlign = node.attrs?.textAlign;
          const paragraphStyle: React.CSSProperties = {};
          if (textAlign) paragraphStyle.textAlign = textAlign as any;
          if (node.attrs?.color) paragraphStyle.color = node.attrs.color;
          if (node.attrs?.backgroundColor)
            paragraphStyle.backgroundColor = node.attrs.backgroundColor;

          return (
            <p
              style={
                Object.keys(paragraphStyle).length > 0
                  ? paragraphStyle
                  : undefined
              }
              data-text-align={textAlign}
            >
              {children}
            </p>
          );

        case "heading":
          const level = node.attrs?.level || 1;
          const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
          const headingTextAlign = node.attrs?.textAlign;
          const headingStyle: React.CSSProperties = {};

          if (headingTextAlign)
            headingStyle.textAlign = headingTextAlign as any;
          if (node.attrs?.color) headingStyle.color = node.attrs.color;
          if (node.attrs?.backgroundColor)
            headingStyle.backgroundColor = node.attrs.backgroundColor;
          if (node.attrs?.fontSize) headingStyle.fontSize = node.attrs.fontSize;
          if (node.attrs?.fontFamily)
            headingStyle.fontFamily = node.attrs.fontFamily;

          return (
            <HeadingTag
              style={
                Object.keys(headingStyle).length > 0 ? headingStyle : undefined
              }
              data-text-align={headingTextAlign}
            >
              {children}
            </HeadingTag>
          );

        default:
          return <div>{children}</div>;
      }
    }

    return null;
  };

  return (
    <div className="editor-container readonly">
      {content.content.map((node, index) => (
        <React.Fragment key={index}>{renderNode(node)}</React.Fragment>
      ))}
    </div>
  );
};

export default BodyDocRenderer;
